import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SnackBarService } from "../../../common/services/snack-bar/snack-bar.service";
import { Email } from './models/email.model';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { VehicleState } from '../../../store/vehicle-store/vehicle-state.model';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { CustomerDispatcher } from '../../../store/customer/customer-dispatcher';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SelectedVehicleState } from '../../../store/selected-vehicle-store/selected-vehicle-state.model';
import { SidenavService } from '../../services/sidenav.service';
import { AdminDispatcher } from '../../../store/admin-store/admin-dispatcher';
import { FinegrainRolesService } from '../../../common/services/finegrain-roles/finegrain-roles.service';
import { NgIf } from '@angular/common';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { RemoteCommandComponent } from '../remote-command/remote-command.component';
import { TranslatePipe } from '@ngx-translate/core';


@Component({
    selector: 'app-remote-status',
    templateUrl: './remote-status.component.html',
    styleUrls: ['./remote-status.component.scss'],
    imports: [NgIf, MatCard, MatCardTitle, MatCardContent, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatIcon, MatButton, RemoteCommandComponent, TranslatePipe]
})
export class RemoteStatusComponent implements OnInit, OnDestroy {
  public hasError: boolean = false;
  /**
  *@property status of the remote service
  **/
  public remoteStatus: string;
  /**
  * @property Will contain vehicle details payload
  */
  @Input() vehicleDetails: object;
  /**
 *@var Declaration of spinner Ref
 **/
  public spinnerRef: SpinnerRef;
  /**
  * Property to observe the redux customer store
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
* Property to observe the redux customer store
**/
  @select('vehicle') readonly vehicleObservable: Observable<VehicleState>;
  /**
  *@property remoteUser to store the form of email and name
  */
  public remoteUser: UntypedFormGroup;
  /**
  *@property model to store the model (contact)
  **/
  public model: any;
  /**
   * @property flag to show remote history
   */
  public showRemoteHistory: boolean;
  /**
   * @property to store the subscriptions
   */
  private apiSubscription: Subscription;
  /**
  *@arg selectedVehicleObservable to observe selectedVehicle observable
  **/
  @select('selectedVehicle') readonly selectedVehicleObservable: Observable<SelectedVehicleState>;
  /**
   * @property used to store the  subscription
   */
  public selectedVehicleSubscriber: Subscription;
  /*
   * @property flag to show authorization
   */
  public isAuthorized: boolean;
  constructor(
    private snackBarService: SnackBarService,
    public translate: TranslationService,
    public customerDispatcher: CustomerDispatcher,
    private apiDispatcher: ApiResponseDispatcher,
    private adminDispatcher: AdminDispatcher,
    public orch: OrchestrationService,
    public fb: UntypedFormBuilder,
    public spinner: SpinnerService,
    private sidenav: SidenavService,
    public finegrainRolesService: FinegrainRolesService
  ) {
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (!!this.spinnerRef) { this.spinnerRef.close(); }
      if (api.apiAction === ApiResponseActions.SET_REMOTE_CUSTOMER) {
        const temp = api.remoteCustomer['type'] === 'success' && !!api.remoteCustomer['customer'] ?
          this.findRemoteCustomerSuccess(api.remoteCustomer['customer']) : api.remoteCustomer['type'] === 'error' ? this.findRemoteCustomerError(api.remoteCustomer) : null;
      }
    });
    this.remoteUser = undefined;
  }
  ngOnInit() {
    this.vehicleObservable.subscribe((vehicleStore) => {
      if (!!vehicleStore && vehicleStore.remote.remoteUserGuid) {
        if (!!vehicleStore.remote.remoteSubscriptionStatus) {
          const status = this.normalizeRemoteStatus(vehicleStore.remote.remoteSubscriptionStatus.trim());
          this.remoteStatus = 'remote-status.label.'.concat(status);
          this.vehicleDetails['remoteSubscriptionStatus'] = status;
        }
        this.checkAuthorization();
      }
    });
    this.model = new Email({ firstName: '', emails: '', guid: '' });
    this.remoteUser = this.fb.group(this.model.form);
    this.selectedVehicleSubscriber = this.selectedVehicleObservable.subscribe({
      next: ({ vehicle }) => {
        const status = this.normalizeRemoteStatus(vehicle['remoteSubscriptionStatus']);
        this.remoteStatus = status ? 'remote-status.label.'.concat(status) : '';
        if (!!vehicle['remoteUserGuid']) {
          this.getCustomerData(vehicle);
          this.checkAuthorization();
        }
      },
      complete: () => this.selectedVehicleSubscriber.unsubscribe()
    });
  }
  /**
   * ondestroy method
   */
  ngOnDestroy() {
    this.selectedVehicleSubscriber.unsubscribe();
    this.apiSubscription.unsubscribe();
  }
  /**
   * @function getCustomerData Get the customer info from redux
   **/
  public getCustomerData(response): void {
    this.orch.searchCustomer('remote', { guid: response.remoteUserGuid });
  }

  /**
   * @function findRemoteCustomerSuccess Action after a successful API response
   * @param response  Response of the API
   **/
  public findRemoteCustomerSuccess(customer: any): void {
    const status = this.normalizeRemoteStatus(this.vehicleDetails && this.vehicleDetails['remoteSubscriptionStatus']);
    this.remoteStatus = 'remote-status.label.'.concat(status);
    if (this.remoteUser) {
      this.remoteUser.setValue({
        firstName: `${customer.firstName} ${customer.lastName}`,
        emails: customer.emails[0].emailAddress,
        guid: customer.guid
      });
    }
  }
  /**
  * @function errorCallback Action for error after API response
  * @param error  Response of the API
  **/
  public findRemoteCustomerError(error: object): void {
    this.hasError = true;
    this.apiDispatcher.setRemoteCustomerResponse({});
    this.snackBarService.showToast('Remote services data failed to load.');
    if (error['customerFound'] === false) {
      this.customerDispatcher.customerNotFoundError(true)
    }
  }
  /**
   * @function showHistory  sets the flag to show the history (remote commands)
   */
  public showHistory(): void {
    this.showRemoteHistory = !this.showRemoteHistory;
  }
  /**
   * @function refreshHistory gets the history from the api
   */
  public refreshHistory(): void {
    this.showRemoteHistory = false;
    setTimeout(() => {
      this.showRemoteHistory = true;
    }, 1);
  }
  /**
 * @method toggleSidenav toggles the side nav
 **/
  public toggleSidenav(updateAttribute: string): void {
    this.adminDispatcher.setUpdateInfo(
      'remote',
      updateAttribute,
      this.remoteUser.value.emails,
      '',
      {
        vin: this.vehicleDetails['vin'],
        guid: this.vehicleDetails['remoteUserGuid'],
        generation: this.vehicleDetails['generation'],
        brand: this.vehicleDetails['brand']
      }
    );
    this.sidenav.toggle();
    window.scrollTo(0, 0);
  }
  /**
* @method checkAuthorization sets true if status is AUTH_REQUIRED or FAILED or INACTIVE
**/
  public checkAuthorization(): void {
    this.isAuthorized =
      !!this.vehicleDetails && ['AUTH_REQUIRED', 'FAILED', 'AUTHORIZATION REQUIRED'].includes(
        this.vehicleDetails['remoteSubscriptionStatus']
      ) && this.finegrainRolesService.getCustomPermissions('authorizeRemote') &&
      ['17CYPLUS', 'GR86', '17CY', '21MM'].includes(this.vehicleDetails['generation']);
  }

  /**
   * @function getPermission get Permissions to display features
   */
  public getPermission(feature: string): boolean {
    return this.finegrainRolesService.getCustomerPermissions(feature);
  }

  /**
   * @function normalizeRemoteStatus Maps non-standard backend statuses to known values.
   * SUCCESS is returned by the subscription service in place of ACTIVE for some vehicles.
   */
  private normalizeRemoteStatus(status: string): string {
    return status === 'SUCCESS' ? 'ACTIVE' : status;
  }

  /**
 * @method toggleUpdateRemoteUserSidenav toggles the side nav to update the remote user
 **/
  public toggleUpdateRemoteUserSidenav(): void {
    this.adminDispatcher.setUpdateInfo('update-remote-user', 'update-remote-user', this.vehicleDetails['vin'], '');
    this.sidenav.toggle();
    window.scrollTo(0, 0);
  }
}
